import { useModeAwareQuery } from "./useModeAwareQuery";
import { useApi } from "./useApi";
import { getDisputeNotes } from "@/lib/demo-data/dispute-notes";
import type { DisputeNote, DisputeNoteApiItem, DisputeNotesApiResponse, CreateDisputeNoteRequest } from "@/types/dispute-note";
import { useMutation, useQueryClient } from "@tanstack/react-query";

function mapApiItemToDisputeNote(item: DisputeNoteApiItem): DisputeNote {
  return {
    id: String(item.id),
    disputeId: String(item.disputeId),
    content: item.content,
    createdAt: item.createdAt,
    createdBy: {
      id: String(item.createdByUserId),
      name: item.createdByUserName,
    },
  };
}

/**
 * Hook to fetch dispute notes
 * Handles both Demo and Live modes
 */
export function useDisputeNotes(disputeId: string | undefined) {
  const { apiGet } = useApi();
  const mockNotes = disputeId ? getDisputeNotes(disputeId) : [];

  return useModeAwareQuery<DisputeNote[], Error>(
    ["dispute-notes", disputeId],
    async ({ signal }) => {
      if (!disputeId) return [];
      const response = await apiGet<DisputeNotesApiResponse>(
        `/api/v1/disputes/${disputeId}/notes`,
        undefined,
        signal
      );
      return (response.notes || []).map(mapApiItemToDisputeNote);
    },
    mockNotes,
    {
      enabled: !!disputeId,
      staleTime: 0,
      refetchOnMount: 'always',
    }
  );
}

/**
 * Hook to add a new dispute note
 * Handles both Demo and Live modes
 */
export function useAddDisputeNote() {
  const { apiPost } = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: CreateDisputeNoteRequest & { userId: string; userName: string }) => {
      const response = await apiPost<DisputeNoteApiItem>(
        `/api/v1/disputes/${request.disputeId}/notes`,
        {
          content: request.content,
        }
      );
      return mapApiItemToDisputeNote(response);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["dispute-notes", variables.disputeId] });
    },
  });
}
